import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const FEES_ACCESS_KEY = "asha_os_fees_yer2453_access";

const STYLES = `
.pms-quote-doc {
  --paper: #fcfcfd;
  --paper-2: #f4f5f8;
  --ink: #1c2540;
  --ink-2: #404a66;
  --ink-3: #737d96;
  --line: #d8dde6;
  --line-2: #e7eaf0;
  --accent: #3a5bd9;
  --accent-deep: #2638b2;
  --accent-soft: #eef0fa;
  --accent-ink: #2c43c4;
  --accent-soft-2: #eaedf7;
  --accent-border: #bdc6e8;

  font-family: 'Inter', system-ui, sans-serif;
  color: var(--ink);
  font-size: 14px;
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-feature-settings: 'ss01', 'cv11';
}

.pms-quote-doc *,
.pms-quote-doc *::before,
.pms-quote-doc *::after { box-sizing: border-box; }

.pms-quote-doc .page {
  max-width: 1040px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(0,0,0,0.02),
    0 30px 60px -30px rgba(20,30,40,0.18),
    0 12px 24px -16px rgba(20,30,40,0.10);
  overflow: hidden;
}

.pms-quote-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.pms-quote-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .brand-mark {
  width: 28px; height: 28px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
}
.pms-quote-doc .brand-mark img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.pms-quote-doc .brand-meta {
  color: var(--ink-3);
  font-weight: 450;
}
.pms-quote-doc .topbar-right {
  display: flex;
  gap: 12px;
  align-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-3);
}
.pms-quote-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.pms-quote-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(238, 240, 250, 0.7);
}

.pms-quote-doc .hero {
  padding: 72px 56px 56px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(1200px 320px at 90% -100px, rgba(216, 222, 248, 0.55), transparent 60%),
    var(--paper);
}
.pms-quote-doc .eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 18px;
}
.pms-quote-doc .eyebrow b { color: var(--accent-ink); font-weight: 500; }
.pms-quote-doc h1.title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.035em;
  margin: 0 0 14px;
  color: var(--ink);
  max-width: 18ch;
}
.pms-quote-doc .title-sub {
  font-family: 'Fraunces', serif;
  font-style: italic;
  font-weight: 300;
  font-size: 24px;
  letter-spacing: -0.01em;
  color: var(--accent-ink);
  margin: 0 0 26px;
}
.pms-quote-doc .lede {
  font-size: 17px;
  line-height: 1.55;
  color: var(--ink-2);
  max-width: 62ch;
  margin: 0 0 48px;
  text-wrap: pretty;
}

.pms-quote-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  margin-top: 8px;
}
.pms-quote-doc .meta-foot {
  margin: 14px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-3);
  font-style: italic;
}
.pms-quote-doc .meta-cell {
  padding: 18px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .meta-cell:first-child { padding-right: 36px; }
.pms-quote-doc .meta-cell:not(:first-child) { padding-left: 36px; padding-right: 36px; }
.pms-quote-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.pms-quote-doc .meta-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
}
.pms-quote-doc .meta-value {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .meta-value.placeholder {
  color: var(--ink-3);
  font-weight: 400;
}

.pms-quote-doc section.s {
  padding: 64px 56px;
  border-bottom: 1px solid var(--line);
}
.pms-quote-doc section.s:last-of-type { border-bottom: 0; }
.pms-quote-doc .s-head {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 32px;
  margin-bottom: 36px;
  align-items: baseline;
}
.pms-quote-doc .s-num {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding-top: 10px;
  border-top: 1px solid var(--ink);
  width: 52px;
}
.pms-quote-doc .s-title {
  font-family: 'Inter Tight', sans-serif;
  font-size: 34px;
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .s-sub {
  font-size: 15px;
  color: var(--ink-2);
  margin-top: 10px;
  max-width: 60ch;
  text-wrap: pretty;
}
.pms-quote-doc .s-sub b { color: var(--ink); font-weight: 500; }
.pms-quote-doc .s-body {
  margin-left: 152px;
}

.pms-quote-doc .incl-grid {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: 0;
  border-top: 1px solid var(--line);
}
.pms-quote-doc .incl-col {
  padding: 28px 32px 28px 0;
  border-right: 1px solid var(--line-2);
}
.pms-quote-doc .incl-col + .incl-col {
  padding-left: 32px;
  padding-right: 0;
  border-right: 0;
}
.pms-quote-doc .incl-col-head {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;
  row-gap: 2px;
  align-items: center;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc .incl-count {
  grid-row: 1 / span 2;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 38px;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--ink);
}
.pms-quote-doc .incl-count.accent { color: var(--accent); }
.pms-quote-doc .incl-col-title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
  align-self: end;
}
.pms-quote-doc .incl-col-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  align-self: start;
}

.pms-quote-doc .prod-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pms-quote-doc .prod-list li {
  padding-bottom: 14px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .prod-list li:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .prod-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pms-quote-doc .prod-sub {
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.5;
}
.pms-quote-doc .soon {
  display: inline-flex;
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-family: 'JetBrains Mono', monospace;
  font-size: 9.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 500;
}

.pms-quote-doc .agent-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  counter-reset: agent;
}
.pms-quote-doc .agent-list li {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 13.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
  padding: 11px 14px 11px 28px;
  border-bottom: 1px solid var(--line-2);
  position: relative;
  counter-increment: agent;
}
.pms-quote-doc .agent-list li::before {
  content: counter(agent, decimal-leading-zero);
  position: absolute;
  left: 0;
  top: 12px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--ink-3);
  font-weight: 400;
  letter-spacing: 0.04em;
}
.pms-quote-doc .agent-list li:nth-last-child(-n+2) { border-bottom: 0; }
.pms-quote-doc .agent-list li:nth-child(odd) { padding-right: 18px; }

.pms-quote-doc .piecemeal-line {
  font-family: 'Inter', sans-serif;
  font-size: 19px;
  line-height: 1.6;
  color: var(--ink-2);
  margin: 0 0 28px;
  padding: 0 0 18px;
  border-bottom: 1px solid var(--line);
}
.pms-quote-doc .piecemeal-line b {
  color: var(--ink);
  font-weight: 600;
}
.pms-quote-doc .piecemeal-line .pml-meta {
  color: var(--ink-3);
  font-size: 16px;
  display: inline;
}

.pms-quote-doc .vendor-s {
  background: linear-gradient(180deg, var(--paper) 0%, #f7f7fb 100%);
}
.pms-quote-doc .vendor-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.pms-quote-doc .vendor-table thead th {
  text-align: left;
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.pms-quote-doc .vendor-table thead th.vt-price { text-align: right; padding-right: 0; }
.pms-quote-doc .vendor-table tbody td {
  padding: 16px 0;
  border-bottom: 1px solid var(--line-2);
  vertical-align: top;
}
.pms-quote-doc .vendor-table .vt-cat { width: 38%; padding-right: 24px; }
.pms-quote-doc .vendor-table .vt-vendor { width: 36%; padding-right: 24px; color: var(--ink-2); font-size: 13px; }
.pms-quote-doc .vendor-table .vt-price {
  width: 26%;
  text-align: right;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 17px;
  letter-spacing: -0.015em;
  color: var(--ink);
  white-space: nowrap;
}
.pms-quote-doc .vendor-table .vt-price small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}
.pms-quote-doc .vendor-table .vt-name {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.005em;
  color: var(--ink);
}
.pms-quote-doc .vendor-table .vt-meta {
  font-size: 11.5px;
  color: var(--ink-3);
  margin-top: 3px;
  line-height: 1.45;
}
.pms-quote-doc .vendor-table tr.muted .vt-cat .vt-name { color: var(--ink-3); font-weight: 450; }
.pms-quote-doc .vendor-table tr.muted .vt-price { color: var(--ink-3); font-weight: 400; font-size: 14px; }
.pms-quote-doc .vendor-table tr:last-child td { border-bottom: 0; }

.pms-quote-doc .vendor-foot {
  margin-top: 18px;
  font-size: 12px;
  color: var(--ink-3);
  line-height: 1.55;
  max-width: 80ch;
}

.pms-quote-doc .price-primer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0;
  align-items: stretch;
  margin-bottom: 36px;
}
.pms-quote-doc .prim-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 26px 28px;
  background: var(--paper);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pms-quote-doc .prim-card.accent {
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.7));
  border-color: var(--accent-border);
}
.pms-quote-doc .prim-tag {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.pms-quote-doc .prim-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .prim-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 36px;
  letter-spacing: -0.025em;
  color: var(--ink);
  line-height: 1;
  margin-top: 4px;
}
.pms-quote-doc .prim-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 6px;
  letter-spacing: 0;
}
.pms-quote-doc .prim-amount .strike {
  display: inline-block;
  font-size: 18px;
  font-weight: 400;
  color: var(--ink-3);
  text-decoration: line-through;
  margin-right: 10px;
  letter-spacing: -0.01em;
}
.pms-quote-doc .prim-desc {
  font-size: 12.5px;
  color: var(--ink-2);
  line-height: 1.55;
  margin-top: 6px;
}
.pms-quote-doc .prim-desc b { color: var(--ink); font-weight: 500; }
.pms-quote-doc .prim-plus {
  display: grid;
  place-items: center;
  width: 56px;
  font-family: 'Inter Tight', sans-serif;
  font-weight: 300;
  font-size: 28px;
  color: var(--ink-3);
}

.pms-quote-doc .tier-mini-head {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 10px;
  padding-top: 4px;
}

/* Concessions comparison table */
.pms-quote-doc .aif-table {
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  background: var(--paper);
}
.pms-quote-doc .aif-row {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr;
  align-items: center;
  padding: 16px 22px;
  border-bottom: 1px solid var(--line-2);
  gap: 24px;
}
.pms-quote-doc .aif-row:last-child { border-bottom: 0; }
.pms-quote-doc .aif-row.head {
  padding-top: 14px; padding-bottom: 14px;
  background: var(--paper-2);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.pms-quote-doc .aif-row.accent-row {
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.5));
}
.pms-quote-doc .aif-band {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pms-quote-doc .aif-band-sub {
  font-size: 12px;
  color: var(--ink-3);
  margin-top: 2px;
}
.pms-quote-doc .aif-fee {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 19px;
  letter-spacing: -0.02em;
  color: var(--ink);
}
.pms-quote-doc .aif-fee small {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-left: 4px;
  letter-spacing: 0;
}
.pms-quote-doc .aif-fee.struck {
  color: var(--ink-3);
  text-decoration: line-through;
  font-weight: 400;
  font-size: 16px;
}
.pms-quote-doc .aif-fee.accent {
  color: var(--accent-ink);
}
.pms-quote-doc .aif-col-h {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
}
.pms-quote-doc .aif-col-h.accent { color: var(--accent-ink); }

/* Eligibility callout */
.pms-quote-doc .elig-callout {
  border: 1px solid var(--accent-border);
  background: linear-gradient(180deg, var(--accent-soft), rgba(234, 237, 247, 0.6));
  border-radius: 12px;
  padding: 22px 26px;
  margin-bottom: 28px;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 22px;
  align-items: center;
}
.pms-quote-doc .elig-num {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 44px;
  letter-spacing: -0.04em;
  line-height: 1;
  color: var(--accent-ink);
  padding-right: 22px;
  border-right: 1px solid var(--accent-border);
}
.pms-quote-doc .elig-num small {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-ink);
  margin-top: 6px;
  letter-spacing: 0.1em;
}
.pms-quote-doc .elig-body {
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ink-2);
}
.pms-quote-doc .elig-body b {
  color: var(--ink);
  font-weight: 500;
}

/* Concessions list */
.pms-quote-doc .conc-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-top: 1px solid var(--line);
}
.pms-quote-doc .conc-list li {
  padding: 22px 0;
  border-bottom: 1px solid var(--line-2);
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 28px;
  align-items: baseline;
}
.pms-quote-doc .conc-list li:last-child {
  border-bottom: 0;
}
.pms-quote-doc .conc-tag {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  padding-top: 4px;
}
.pms-quote-doc .conc-tag.accent {
  color: var(--accent-ink);
}
.pms-quote-doc .conc-title {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 17px;
  letter-spacing: -0.012em;
  color: var(--ink);
  margin: 0 0 6px;
}
.pms-quote-doc .conc-desc {
  font-size: 13.5px;
  color: var(--ink-2);
  line-height: 1.55;
  max-width: 78ch;
  margin: 0;
}
.pms-quote-doc .conc-desc b { color: var(--ink); font-weight: 500; }

.pms-quote-doc .fineprint {
  list-style: none;
  padding: 22px 0 0;
  margin: 32px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid var(--line-2);
}
.pms-quote-doc .fineprint li {
  font-size: 12.5px;
  color: var(--ink-3);
  line-height: 1.6;
  max-width: 88ch;
  padding-left: 18px;
  position: relative;
}
.pms-quote-doc .fineprint li::before {
  content: "";
  position: absolute;
  left: 0; top: 9px;
  width: 4px; height: 4px;
  border-radius: 99px;
  background: var(--ink-3);
}
.pms-quote-doc .fineprint .fp-label {
  color: var(--ink);
  font-weight: 500;
  font-family: 'Inter Tight', sans-serif;
  margin-right: 4px;
}
.pms-quote-doc .fineprint b { color: var(--ink); font-weight: 500; }

.pms-quote-doc table.lines {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
}
.pms-quote-doc table.lines th,
.pms-quote-doc table.lines td {
  text-align: left;
  padding: 22px 0;
  vertical-align: top;
  border-bottom: 1px solid var(--line-2);
}
.pms-quote-doc table.lines thead th {
  padding: 0 0 14px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  border-bottom: 1px solid var(--ink);
}
.pms-quote-doc table.lines tbody tr:last-child td { border-bottom: 0; }
.pms-quote-doc .li-name {
  font-family: 'Inter Tight', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin-bottom: 6px;
}
.pms-quote-doc .li-desc {
  font-size: 13px;
  color: var(--ink-2);
  line-height: 1.5;
  max-width: 52ch;
}
.pms-quote-doc .li-amount {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: var(--ink);
  white-space: nowrap;
}
.pms-quote-doc .li-amount small {
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-3);
  margin-top: 2px;
  letter-spacing: 0;
}
.pms-quote-doc .li-note {
  font-size: 12.5px;
  color: var(--ink-2);
  line-height: 1.5;
}
.pms-quote-doc .col-item { width: 40%; padding-right: 32px !important; }
.pms-quote-doc .col-billing { width: 26%; padding-right: 32px !important; }
.pms-quote-doc .col-amount { width: 22%; text-align: right !important; padding-right: 32px !important; }
.pms-quote-doc .col-notes { width: 34%; padding-left: 0 !important; }
.pms-quote-doc td.col-amount,
.pms-quote-doc th.col-amount { text-align: right !important; }

.pms-quote-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper);
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
}

/* FAQ section */
.pms-quote-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 22px;
  border-top: 1px solid var(--line);
  padding-top: 28px;
}
.pms-quote-doc .faq-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  padding-bottom: 22px;
  border-bottom: 1px dashed var(--line-2);
}
.pms-quote-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.pms-quote-doc .faq-q {
  font-family: 'Inter Tight', sans-serif;
  font-weight: 500;
  font-size: 18px;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0;
}
.pms-quote-doc .faq-a {
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-2);
  max-width: 80ch;
  margin: 0;
}
.pms-quote-doc .faq-a b { color: var(--ink); font-weight: 500; }

@media (max-width: 900px) {
  .pms-quote-doc .page { margin: 24px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail { padding-left: 28px; padding-right: 28px; }
  .pms-quote-doc h1.title { font-size: 44px; }
  .pms-quote-doc .s-head { grid-template-columns: 1fr; gap: 12px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .pms-quote-doc .incl-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .incl-col { padding: 24px 0; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .price-primer { grid-template-columns: 1fr; }
  .pms-quote-doc .prim-plus { width: auto; padding: 8px 0; }
  .pms-quote-doc .conc-list li { grid-template-columns: 1fr; gap: 8px; }
  .pms-quote-doc .elig-callout { grid-template-columns: 1fr; gap: 16px; }
  .pms-quote-doc .elig-num { border-right: 0; padding-right: 0; padding-bottom: 12px; border-bottom: 1px solid var(--accent-border); }
}

@media (max-width: 720px) {
  /* Tighter outer chrome */
  .pms-quote-doc .page { margin: 14px; border-radius: 14px; }
  .pms-quote-doc .hero,
  .pms-quote-doc section.s,
  .pms-quote-doc .topbar,
  .pms-quote-doc .tail {
    padding-left: 20px; padding-right: 20px;
  }
  .pms-quote-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .pms-quote-doc .topbar-right { gap: 8px; }
  .pms-quote-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .pms-quote-doc section.s { padding-top: 40px; padding-bottom: 40px; }

  /* Hero scales down */
  .pms-quote-doc h1.title { font-size: 34px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 18px; margin-bottom: 20px; }
  .pms-quote-doc .lede { font-size: 15px; margin-bottom: 32px; }

  /* Single-column meta grid with horizontal dividers */
  .pms-quote-doc .meta-grid { grid-template-columns: 1fr; }
  .pms-quote-doc .meta-cell {
    padding: 14px 0 !important;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .meta-cell:last-child { border-bottom: 0; }

  /* Stack the .lines table (Service / [Billing] / Amount [/ Notes]) into cards */
  .pms-quote-doc table.lines,
  .pms-quote-doc table.lines tbody,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc table.lines td {
    display: block;
    width: auto !important;
  }
  .pms-quote-doc table.lines thead { display: none; }
  .pms-quote-doc table.lines tr {
    padding: 22px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc table.lines tbody tr:first-child { padding-top: 18px; }
  .pms-quote-doc table.lines tbody tr:last-child { border-bottom: 0; }
  .pms-quote-doc table.lines td {
    padding: 0 !important;
    border: 0 !important;
  }
  .pms-quote-doc table.lines td + td { margin-top: 14px; }
  .pms-quote-doc table.lines td.col-amount { text-align: left !important; }
  .pms-quote-doc .li-desc,
  .pms-quote-doc .li-note { max-width: none; }

  /* Stack the vendor-table into cards as well */
  .pms-quote-doc .vendor-table,
  .pms-quote-doc .vendor-table tbody,
  .pms-quote-doc .vendor-table tr,
  .pms-quote-doc .vendor-table td {
    display: block;
    width: auto !important;
  }
  .pms-quote-doc .vendor-table thead { display: none; }
  .pms-quote-doc .vendor-table tbody tr {
    padding: 18px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .vendor-table tbody tr:last-child { border-bottom: 0; }
  .pms-quote-doc .vendor-table tbody td {
    padding: 0 !important;
    border: 0 !important;
  }
  .pms-quote-doc .vendor-table tbody td + td { margin-top: 8px; }
  .pms-quote-doc .vendor-table .vt-price { text-align: left; }

  /* AIF compare table — stack to two columns under header */
  .pms-quote-doc .aif-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 14px 16px;
    align-items: start;
  }
  .pms-quote-doc .aif-row > div:first-child {
    grid-column: 1 / -1;
  }
  .pms-quote-doc .aif-row.head {
    grid-template-columns: 1fr 1fr;
  }
  .pms-quote-doc .aif-row.head > div:first-child {
    display: none;
  }
  .pms-quote-doc .aif-fee { font-size: 17px; }

  /* Footer wraps cleanly instead of squeezing 3 spans onto one line */
  .pms-quote-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }

}

@media print {
  @page {
    size: letter;
    margin: 0.3in;
  }
  html, body {
    background: #fff !important;
  }
  body.pms-quote-body { background: #fff !important; }
  .pms-quote-doc { font-size: 11px !important; }
  .pms-quote-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .pms-quote-doc .topbar { padding: 14px 24px; }
  .pms-quote-doc .hero { padding: 32px 24px 24px; }
  .pms-quote-doc h1.title { font-size: 32px; max-width: none; }
  .pms-quote-doc .title-sub { font-size: 16px; margin-bottom: 14px; }
  .pms-quote-doc .lede { margin-bottom: 20px; font-size: 12px; }
  .pms-quote-doc section.s { padding: 24px; }
  .pms-quote-doc .s-head {
    grid-template-columns: 1fr;
    gap: 8px;
    margin-bottom: 16px;
  }
  .pms-quote-doc .s-title { font-size: 22px; }
  .pms-quote-doc .s-body { margin-left: 0; }
  .pms-quote-doc .tail { padding: 12px 24px; }

  .pms-quote-doc .meta-grid { grid-template-columns: repeat(2, 1fr); }
  .pms-quote-doc .incl-grid { display: block; }
  .pms-quote-doc .incl-col {
    padding: 18px 0;
    border-right: 0;
    border-bottom: 1px solid var(--line-2);
  }
  .pms-quote-doc .incl-col:last-child { border-bottom: 0; }
  .pms-quote-doc .incl-col + .incl-col { padding-left: 0; }
  .pms-quote-doc .agent-list { grid-template-columns: 1fr 1fr 1fr; }

  .pms-quote-doc .meta-grid,
  .pms-quote-doc .prim-card,
  .pms-quote-doc .aif-row,
  .pms-quote-doc .vendor-table tr,
  .pms-quote-doc table.lines tr,
  .pms-quote-doc .fineprint li,
  .pms-quote-doc .prod-list li,
  .pms-quote-doc .agent-list li,
  .pms-quote-doc .conc-list li,
  .pms-quote-doc .elig-callout {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .pms-quote-doc h1.title,
  .pms-quote-doc .s-title,
  .pms-quote-doc .prod-name,
  .pms-quote-doc .li-name,
  .pms-quote-doc .vt-name,
  .pms-quote-doc .incl-col-head,
  .pms-quote-doc .s-head {
    break-after: avoid;
    page-break-after: avoid;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

const OsFeesYer2453Page = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (hasStoredContentAccess(FEES_ACCESS_KEY)) setIsLocked(false);
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FEES_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Fee schedule access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const link1 = document.createElement("link");
    link1.rel = "preconnect";
    link1.href = "https://fonts.googleapis.com";

    const link2 = document.createElement("link");
    link2.rel = "preconnect";
    link2.href = "https://fonts.gstatic.com";
    link2.crossOrigin = "";

    const link3 = document.createElement("link");
    link3.rel = "stylesheet";
    link3.href =
      "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Inter:wght@400;450;500;600&family=JetBrains+Mono:wght@400;500&family=Fraunces:opsz,wght@9..144,300;9..144,400&display=swap";

    document.head.appendChild(link1);
    document.head.appendChild(link2);
    document.head.appendChild(link3);

    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#f1efe7";
    document.body.style.margin = "0";
    document.body.classList.add("pms-quote-body");
    document.title = "Asha — Fee Schedule";

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(link3);
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("pms-quote-body");
    };
  }, []);

  return (
    <div className="pms-quote-doc">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {isLocked && <EmailGate onAccessGranted={handleAccessGranted} />}

      <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <div className="topbar-right">
            <span className="pill">AI Innovation Fellows</span>
            <span className="pill">Confidential</span>
          </div>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="eyebrow">
            <b>Fee Schedule</b> &nbsp;·&nbsp; <b>AI Innovation Fellows</b> Pricing
          </div>
          <h1 className="title">
            The AI Operating System for Modern Dental Offices
          </h1>
          <div className="title-sub">
            10 softwares + 20 AI products in 1 unified platform
          </div>
          <p className="lede">
            This fee schedule outlines pricing for the Asha AI-Native PMS and
            our full front-office and back-office AI agent suite. Pricing is
            per location; practices operating multiple locations are billed
            per location. Final terms are governed by the executed Statement
            of Work and the Asha Terms of Service.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">Asha Health, Inc.</div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Fees valid through</div>
              <div className="meta-value">Q2 2026</div>
            </div>
          </div>
          <p className="meta-foot">
            Fees subject to an increase for offices signing up after Q2 2026.
          </p>
        </div>

        {/* § 01 — INCLUDED */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 01</div>
            <div>
              <h2 className="s-title">What's included</h2>
              <p className="s-sub">
                Every location plan ships with the full Asha platform — the
                PMS, both AI agent suites, and our patient-facing
                communication and payments tooling.
              </p>
            </div>
          </div>
          <div className="s-body">
            <div className="incl-grid">
              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count">10</span>
                  <span className="incl-col-title">Softwares</span>
                  <span className="incl-col-sub">
                    Full platform, no third-party stitching.
                  </span>
                </div>
                <ul className="prod-list">
                  <li>
                    <div className="prod-name">Practice Management System</div>
                    <div className="prod-sub">
                      Patient charts · Perio charting · Ledgers &amp; billing ·
                      Treatment planning · Scheduling · Clinical notes ·
                      Cloud-based · Multi-user access
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Communication Suite</div>
                    <div className="prod-sub">
                      VoIP phone system · Yealink desk phones · 2-way texting ·
                      Universal inbox · Team chat &amp; channels · Smart voice
                      logs · Intelligent fax · Next-gen digital forms
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Marketing Suite</div>
                    <div className="prod-sub">
                      SMS &amp; email campaigns · Automated workflows ·
                      Clinically-aware segmentation · AI handles patient
                      replies
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">eClaims Portal</div>
                    <div className="prod-sub">
                      Built-in eClaims · Clearinghouse connectivity · Single
                      login · Integrated submission
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Revenue Intelligence Suite</div>
                    <div className="prod-sub">
                      Reporting &amp; analytics dashboards · KPI tracking ·
                      Collection-rate insights · Revenue cycle analytics ·
                      Performance metrics
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Cloud Imaging Software</div>
                    <div className="prod-sub">
                      SOTA Cloud Imaging · DICOM compatible · Digital X-ray
                      viewing · Connects to your sensors · Cloud storage
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      Insurance Verification Software
                    </div>
                    <div className="prod-sub">
                      Automated verification · Real-time eligibility · Benefits
                      breakdown upload · Dual-source verification
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Integrated Payments Suite</div>
                    <div className="prod-sub">
                      Text-to-pay · Card readers · Multi-method support ·
                      Automated reminders
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Patient Portal</div>
                    <div className="prod-sub">
                      Secure patient links · Interactive forms · Treatment-plan
                      viewing · Insurance info upload
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">
                      ePrescribe <span className="soon">Coming soon</span>
                    </div>
                    <div className="prod-sub">
                      Electronic prescriptions · Integrated with PMS
                    </div>
                  </li>
                  <li>
                    <div className="prod-name">Online Scheduling</div>
                    <div className="prod-sub">
                      Patient self-scheduling · Real-time availability
                    </div>
                  </li>
                </ul>
              </div>

              <div className="incl-col">
                <div className="incl-col-head">
                  <span className="incl-count accent">20</span>
                  <span className="incl-col-title">AI Agents</span>
                  <span className="incl-col-sub">
                    Front- and back-office automation, end-to-end.
                  </span>
                </div>
                <ul className="agent-list">
                  <li>AI Receptionist</li>
                  <li>AI Schedule Optimizer</li>
                  <li>AI Hygiene Recall</li>
                  <li>AI Patient Experience</li>
                  <li>AI Case Acceptance</li>
                  <li>AI Patient Intake</li>
                  <li>AI Practice Manager</li>
                  <li>AI Fax Manager</li>
                  <li>AI Insurance Verification</li>
                  <li>AI Claim Submission</li>
                  <li>AI Claim Scrubbing</li>
                  <li>AI Denial Management</li>
                  <li>AI Patient Billing</li>
                  <li>AI AR &amp; Collections</li>
                  <li>AI EOB Posting</li>
                  <li>AI Insurance Calls</li>
                  <li>AI Copilot</li>
                  <li>AI Scribe</li>
                  <li>AI Treatment Planner</li>
                  <li>AI Revenue Analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* § 02 — PRICING COMPARISON */}
        <section className="s vendor-s">
          <div className="s-head">
            <div className="s-num">§ 02</div>
            <div>
              <h2 className="s-title">Pricing comparison</h2>
              <p className="s-sub">
                If you tried to assemble what Asha gives you out of the box
                from other vendors, here's what it would actually cost. Using
                Asha replaces this entire stack with a single platform — saving
                your practice significant cost every month.
              </p>
            </div>
          </div>

          <div className="s-body">
            <p className="piecemeal-line">
              <b>
                If you piecemealed this from other vendors, you would pay
                {" "}<span>$8,400</span> +{" "}
                <span>3.5% of collections</span>
              </b>{" "}
              per location, per month...on the low end.
            </p>

            <table className="vendor-table">
              <thead>
                <tr>
                  <th className="vt-cat">Capability</th>
                  <th className="vt-vendor">Vendors</th>
                  <th className="vt-price">Typical price</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Practice Management System</div>
                  </td>
                  <td className="vt-vendor">
                    Dentrix · Eaglesoft · Open Dental
                  </td>
                  <td className="vt-price">
                    $400 – $700<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      Communication Suite (premium)
                    </div>
                  </td>
                  <td className="vt-vendor">Weave · RevenueWell</td>
                  <td className="vt-price">
                    $700 – $1,200<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Revenue Analytics</div>
                  </td>
                  <td className="vt-vendor">Dental Intel</td>
                  <td className="vt-price">
                    $300 – $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      Insurance Verification (software)
                    </div>
                  </td>
                  <td className="vt-vendor">Zuub · Airpay</td>
                  <td className="vt-price">
                    $300 – $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">eClaims Portal</div>
                  </td>
                  <td className="vt-vendor">VyneTrellis · DentalExchange</td>
                  <td className="vt-price">
                    $150 – $250<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">AI Scribe</div>
                  </td>
                  <td className="vt-vendor">
                    Typical scribe vendors · ~$149 / provider
                  </td>
                  <td className="vt-price">
                    ~$300<small>/ mo (2 providers)</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">AI Receptionist</div>
                  </td>
                  <td className="vt-vendor">Dentina · Arini</td>
                  <td className="vt-price">
                    $300 – $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">AI Case Acceptance</div>
                  </td>
                  <td className="vt-vendor">Codent</td>
                  <td className="vt-price">
                    $500<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      AI Schedule Optimizer &amp; Outbound Calls
                    </div>
                  </td>
                  <td className="vt-vendor">PatientDesk</td>
                  <td className="vt-price">
                    $1,000<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">
                      AI Hygiene Recall &amp; Outbound Calls
                    </div>
                  </td>
                  <td className="vt-vendor">Patient Desk</td>
                  <td className="vt-price">
                    $1,000<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">ePrescribe Software</div>
                  </td>
                  <td className="vt-vendor">DoseSpot</td>
                  <td className="vt-price">
                    ~$200<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Insurance Billing Service</div>
                    <div className="vt-meta">
                      Submission, scrubbing, denials, posting
                    </div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    3.5%<small>of collections</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Insurance Verification Calls</div>
                    <div className="vt-meta">
                      Based on $8/patient × 300 patients/mo
                    </div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    $2,400<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Payment Reconciliation</div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    $200<small>/ mo</small>
                  </td>
                </tr>
                <tr>
                  <td className="vt-cat">
                    <div className="vt-name">Patient Billing</div>
                  </td>
                  <td className="vt-vendor">eAssist · Wisdom</td>
                  <td className="vt-price">
                    $1,000<small>/ mo</small>
                  </td>
                </tr>
                <tr className="muted">
                  <td className="vt-cat">
                    <div className="vt-name">AI EOB Posting</div>
                    <div className="vt-meta">
                      Shown separately — not added to total to avoid
                      double-counting against the 3.5% insurance billing line
                      above.
                    </div>
                  </td>
                  <td className="vt-vendor">Lassie</td>
                  <td className="vt-price">
                    2%<small>of collections</small>
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="vendor-foot">
              Figures reflect a representative subset of capabilities Asha
              includes — the full Asha platform covers more. Ranges are typical
              published rack rates and may vary by practice size, region, and
              contract length.
            </p>
          </div>
        </section>

        {/* § 03 — OUR PRICING */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 03</div>
            <div>
              <h2 className="s-title">Our pricing for you</h2>
              <p className="s-sub">
                Your Total Monthly Service Fee combines a fixed Platform Fee
                with an AI Agents Fee that scales with collections. This fee
                schedule is contingent on the Practice signing with a{" "}
                <b>minimum of four (4) locations</b> concurrently under a
                single Statement of Work. Under that commitment, your AI
                Agents rate is discounted from <b>3.5%</b> to <b>2.5%</b>,
                the <b>$65,000 monthly collection floor is waived on your 5th
                location</b>, and the <b>$1,800 one-time Onboarding Fee is
                waived</b>.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="price-primer">
              <div className="prim-card">
                <div className="prim-tag">Component 1</div>
                <div className="prim-name">Asha Platform</div>
                <div className="prim-amount">
                  $800<small>/ location / month</small>
                </div>
                <div className="prim-desc">
                  A fixed monthly fee covering the 10 softwares we replace and
                  unify (PMS, communication suite, marketing suite, revenue
                  intelligence suite, &amp; more).
                </div>
              </div>
              <div className="prim-plus">+</div>
              <div className="prim-card accent">
                <div className="prim-tag">Component 2</div>
                <div className="prim-name">AI Agents Fee</div>
                <div className="prim-amount">
                  <span className="strike">3.5%</span>2.5%<small>of collections · AI Innovation Fellows rate</small>
                </div>
                <div className="prim-desc">
                  A usage-aligned fee for the 20 AI products — which are
                  capable of carrying out 40+ tasks that your humans do.
                  Discounted by <b>1 percentage point</b> due to participation
                  in the <b>AI Innovation Fellows Program</b>.
                </div>
              </div>
            </div>

            <div className="tier-mini-head">
              AI Innovation Fellows discount applied
            </div>

            <div className="aif-table">
              <div className="aif-row head">
                <div className="aif-col-h">Line item</div>
                <div className="aif-col-h">Standard</div>
                <div className="aif-col-h accent">AI Innovation Fellows</div>
              </div>
              <div className="aif-row">
                <div>
                  <div className="aif-band">Platform Fee</div>
                  <div className="aif-band-sub">
                    Fixed monthly fee per location
                  </div>
                </div>
                <div className="aif-fee">
                  $800<small>/ mo</small>
                </div>
                <div className="aif-fee">
                  $800<small>/ mo</small>
                </div>
              </div>
              <div className="aif-row accent-row">
                <div>
                  <div className="aif-band">AI Agents Fee</div>
                  <div className="aif-band-sub">
                    Scales with monthly collections
                  </div>
                </div>
                <div className="aif-fee">
                  3.5%<small>of collections</small>
                </div>
                <div className="aif-fee accent">
                  2.5%<small>of collections</small>
                </div>
              </div>
              <div className="aif-row accent-row">
                <div>
                  <div className="aif-band">Fifth-Location Collections Floor</div>
                  <div className="aif-band-sub">
                    Monthly minimum on the 5th location only
                  </div>
                </div>
                <div className="aif-fee">
                  $65,000<small>/ mo</small>
                </div>
                <div className="aif-fee accent">Waived</div>
              </div>
              <div className="aif-row accent-row">
                <div>
                  <div className="aif-band">Onboarding Fee</div>
                  <div className="aif-band-sub">
                    One-time setup, data migration, configuration &amp;
                    training
                  </div>
                </div>
                <div className="aif-fee">
                  $1,800<small>one-time</small>
                </div>
                <div className="aif-fee accent">Waived</div>
              </div>
            </div>

            <ul className="fineprint">
              <li>
                <span className="fp-label">$65,000 Collection Floor.</span>
                The AI Agents Fee is calculated against the greater of{" "}
                <b>(a)</b> actual monthly collections or <b>(b) $65,000</b> per
                location. This minimum ensures consistent service economics
                across practices of varying scale. Under the{" "}
                <b>AI Innovation Fellows program</b>, the floor is{" "}
                <b>waived for the Practice's 5th location</b>; for that one
                location, the AI Agents Fee is calculated solely against
                actual collections.
              </li>
              <li>
                <span className="fp-label">Monthly Retainer &amp; True-Up.</span>
                On the first day of each billing month, the Practice pays a{" "}
                <b>$2,425</b> Monthly Retainer per floor-bearing location — the
                $800 Platform Fee plus $1,625 toward the AI Agents Fee (2.5% ×
                the $65,000 floor). At month-end, Asha calculates the actual
                AI Agents Fee against collections; any amount above $1,625 is
                invoiced as a true-up. For the 5th location (no floor), the AI
                Agents Fee component of the Monthly Retainer is set to $0 and
                the entire fee is collected at month-end true-up.
              </li>
              <li>
                <span className="fp-label">AI Credits.</span>
                150,000 credits per location are included each month — most
                practices stay well within this allotment. We specify AI credit
                limits because running AI models is expensive and uncapped
                usage would risk us losing money on contracts. Overages bill at{" "}
                <b>$0.023 per 1,000 credits</b> (2.3¢ per 1K). Your account
                manager can share utilization-management techniques &amp; tools
                to help your team stay within the included allotment.
              </li>
            </ul>
          </div>
        </section>

        {/* § 04 — CONCESSIONS */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 04</div>
            <div>
              <h2 className="s-title">AI Innovation Fellows program</h2>
              <p className="s-sub">
                The concessions extended under the AI Innovation Fellows
                program are detailed here in full so there is no ambiguity
                about what is — and is not — included.
              </p>
            </div>
          </div>

          <div className="s-body">
            <ul className="conc-list">
              <li>
                <span className="conc-tag accent">Concession 01</span>
                <div>
                  <h3 className="conc-title">
                    Discounted AI Agents Fee — 2.5% (down from 3.5%)
                  </h3>
                  <p className="conc-desc">
                    The AI Agents Fee is reduced from <b>3.5%</b> to{" "}
                    <b>2.5%</b> of monthly collections per location — a
                    1-percentage-point discount applied uniformly across every
                    location included in the engagement.
                  </p>
                </div>
              </li>
              <li>
                <span className="conc-tag accent">Concession 02</span>
                <div>
                  <h3 className="conc-title">
                    5th-Location Collection-Floor Waiver
                  </h3>
                  <p className="conc-desc">
                    Should the Practice expand and bring on a <b>5th
                    location</b> under this engagement, the{" "}
                    <b>$65,000 monthly collection floor is waived for that
                    location</b>. The AI Agents Fee on the 5th location is
                    calculated solely against actual collections — no
                    minimum applied.
                  </p>
                </div>
              </li>
              <li>
                <span className="conc-tag accent">Concession 03</span>
                <div>
                  <h3 className="conc-title">
                    Onboarding Fee Waiver — $1,800 / location
                  </h3>
                  <p className="conc-desc">
                    The <b>$1,800 one-time Onboarding Fee per location is
                    waived</b> under the AI Innovation Fellows program across
                    every location in the engagement, including the 5th and
                    any subsequent locations.
                  </p>
                </div>
              </li>
              <li>
                <span className="conc-tag">Notice</span>
                <div>
                  <h3 className="conc-title">
                    Floor waiver not guaranteed beyond the 5th location
                  </h3>
                  <p className="conc-desc">
                    The collection-floor waiver applies <b>only to the 5th
                    location</b> and is{" "}
                    <b>not guaranteed for any subsequent locations</b> (6th,
                    7th, and onward). For those locations, the standard{" "}
                    <b>$65,000 monthly collection floor will apply</b> unless
                    separately negotiated in writing as part of an amended
                    Statement of Work.
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </section>

        {/* § 05 — ONE-TIME */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 05</div>
            <div>
              <h2 className="s-title">One-time fees &amp; deposits</h2>
              <p className="s-sub">
                Charged once per location at signing.
              </p>
            </div>
          </div>

          <div className="s-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="col-item">Item</th>
                  <th className="col-amount">Amount</th>
                  <th className="col-notes">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="col-item">
                    <div className="li-name">Deposit</div>
                    <div className="li-desc">
                      Equal to one Monthly Retainer, charged{" "}
                      <b style={{ color: "var(--ink)", fontWeight: 500 }}>
                        per location
                      </b>
                      . Secures your onboarding start date and is applied to
                      the final month of the contract term.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $2,425<small>per location</small>
                    </div>
                  </td>
                  <td className="col-notes">
                    <div className="li-note">
                      Collected upon signing — one deposit per location.
                      Refundable in the form of a credit against the final
                      month's invoice.
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* § 06 — ADD-ONS */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 06</div>
            <div>
              <h2 className="s-title">Optional third-party add-ons</h2>
              <p className="s-sub">
                Available at subsidized rates negotiated by Asha. All fees are
                paid directly to the third-party vendor — Asha does not retain
                any margin.
              </p>
            </div>
          </div>

          <div className="s-body">
            <table className="lines">
              <thead>
                <tr>
                  <th className="col-item">Service</th>
                  <th className="col-billing">Billing</th>
                  <th className="col-amount">Price</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="col-item">
                    <div className="li-name">SOTA Cloud Imaging</div>
                    <div className="li-desc">
                      Optional cloud dental imaging software. Includes the
                      Essentials plan, sufficient for most offices; 3D imaging
                      is available in higher-tier plans.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-note">
                      Monthly per location. Asha pays the first{" "}
                      <b style={{ color: "var(--ink)", fontWeight: 500 }}>
                        3 months
                      </b>{" "}
                      per location; your office pays the remaining months.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $159<small>/ location / month</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">SOTA Cloud Onboarding</div>
                    <div className="li-desc">
                      Optional onboarding &amp; configuration delivered by
                      SOTA, per location.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-note">
                      One-time per location. Paid directly to SOTA.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $495<small>one-time / location</small>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="col-item">
                    <div className="li-name">
                      (Optional) Historical Image Conversion
                    </div>
                    <div className="li-desc">
                      Optional migration of historical imaging data from legacy
                      software into SOTA Cloud.
                    </div>
                  </td>
                  <td className="col-billing">
                    <div className="li-note">
                      One-time per location. Paid directly to SOTA.
                    </div>
                  </td>
                  <td className="col-amount">
                    <div className="li-amount">
                      $995<small>one-time / location</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* § 07 — FAQ */}
        <section className="s">
          <div className="s-head">
            <div className="s-num">§ 07</div>
            <div>
              <h2 className="s-title">Frequently asked questions</h2>
              <p className="s-sub">
                Quick answers to the most common questions about how this fee
                schedule applies to your practice.
              </p>
            </div>
          </div>

          <div className="s-body">
            <div className="faq-list">
              <div className="faq-item">
                <h3 className="faq-q">How are locations determined?</h3>
                <p className="faq-a">
                  Locations follow exactly how your existing practice
                  management system has them configured. Asha maps your data
                  over one-to-one during migration, so{" "}
                  <b>locations cannot be merged or separated</b> as part of
                  onboarding — whatever structure exists in your current PMS
                  is what we mirror in Asha.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What is the AI Innovation Fellows program?
                </h3>
                <p className="faq-a">
                  AI Innovation Fellows is a discounted-pricing track offered
                  to a select group of forward-thinking dental practices
                  partnering with Asha to help shape the AI Operating System.
                  Practices on this track receive a reduced AI Agents Fee
                  (<b>2.5%</b> instead of 3.5%), a one-time waiver of the{" "}
                  <b>$65,000 collection floor on the 5th location</b>, and a
                  waiver of the <b>$1,800 one-time Onboarding Fee per
                  location</b>. For this Practice, eligibility is contingent
                  on signing with at least <b>four (4) locations</b>{" "}
                  concurrently.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  How does the $65,000 collection floor work?
                </h3>
                <p className="faq-a">
                  The 2.5% AI Agents Fee is calculated against the{" "}
                  <b>greater of</b> your actual monthly collections{" "}
                  <b>or $65,000</b> per location. So if a location collects
                  $50,000 in a given month, the AI Agents Fee is still
                  calculated against $65,000 (= $1,625). If collections exceed
                  $65,000, the fee is calculated against actual collections.
                  The floor is <b>waived for the 5th location</b> under the
                  AI Innovation Fellows program.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  Does the floor waiver apply to my 6th, 7th, or later
                  locations?
                </h3>
                <p className="faq-a">
                  No. The collection-floor waiver applies <b>only to the 5th
                  location</b> as a one-time concession.{" "}
                  <b>Subsequent locations (6th, 7th, and beyond) are not
                  guaranteed</b> the same waiver — the standard $65,000
                  collection floor will apply unless separately negotiated in
                  writing.
                </p>
              </div>
              <div className="faq-item">
                <h3 className="faq-q">
                  What happens if we sign fewer than four locations?
                </h3>
                <p className="faq-a">
                  This entire fee schedule — including the discounted AI
                  Agents Fee and the 5th-location floor waiver — is contingent
                  on the Practice signing with a minimum of{" "}
                  <b>four (4) locations</b>. If fewer locations are signed,
                  the concessions are void and{" "}
                  <b>standard pricing applies</b> (3.5% AI Agents Fee, full
                  collection floor on every location).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>
            Confidential — Summary of proposed commercial terms; not a binding
            agreement.
          </span>
          <span>Page 01 / 01</span>
        </div>
      </div>

    </div>
  );
};

export default OsFeesYer2453Page;
